/**
 * 병종 가지의 기호 그림: 창 · 칼 · 활 · 말 머리 · 쇠스랑. 작게 줄여도 읽히는 선 그림이고, 재료마다 색을 입힌다 —
 * 날은 쇠 빛, 자루와 활은 나무, 코등이는 금, 말은 밤색(플레이한 사람: "아이콘에 색깔도 좀 넣어줘"). 색은 테마의 토큰
 * (`--bi-*`, `index.html`)이라 밝게·어둡게에서 저마다 바탕과 견줘 읽히는 색조를 쓴다. 처음에는 글자색 한 색이었다.
 *
 * 출전 위치 패의 병력 구성에 쓴다. 플레이한 사람이 청했다: "병종을 아이콘화 해줘." 두 번 틀렸다 —
 * - 처음에는 한 글자 약칭("도 16 · 기 2")이었다.
 * - 다음에는 병사의 그림을 작게 잘라 넣었다. 가슴 위로 자르면 창병·도수·기병이 같은 투구의 얼굴이고, 몸과 무기가
 *   보이게 잘라도(26×32) 도검은 칼이 몸에 가려 3·4티어에서 궁과 헷갈렸다(QA가 세 나라 × 티어로 봤다). 병사의 그림은
 *   "누구인가"를 말하지 "어느 병종인가"를 말하지 않는다 — 같은 사람이 기호로 바꾸자고 했다.
 */
import type { Branch } from '../sim/troops.ts';

const SVG = 'http://www.w3.org/2000/svg';

/** 재료의 색 이름. `index.html`의 `--bi-<이름>` 토큰과 짝이다. */
type Tone = 'wood' | 'steel' | 'gold' | 'hide' | 'ink';

/** 한 획. `fill`이면 채우고(선은 가늘게), 아니면 선만 긋는다. `w`는 선 굵기(기본 1.8 — 20칸 격자에서). `c`는 재료의 색. */
interface Stroke {
  d: string;
  c: Tone;
  fill?: boolean;
  w?: number;
}

/** 20 × 20 격자에 그린 가지별 그림. */
const SHAPES: Record<Branch, readonly Stroke[]> = {
  // 창: 비스듬한 나무 창대와 쇠 창날. 창날은 뾰족한 잎 모양이다 — 처음의 둥근 날은 16px에서 곤봉처럼 보였다(QA).
  spear: [
    { d: 'M4 16 L12.5 7.5', c: 'wood' },
    { d: 'M11 9 L11.6 5.2 L17 3 L14.8 8.4 Z', c: 'steel', fill: true, w: 1 },
  ],
  // 도검: 쇠 칼날, 금빛 코등이(가로대), 나무 손잡이.
  blade: [
    { d: 'M8.5 11.5 L15.5 4.5 L16.5 3.5 L15.8 6.2 L9.8 12.8 Z', c: 'steel', fill: true, w: 1 },
    { d: 'M6 9.5 L10.5 14', c: 'gold' },
    { d: 'M7.6 12.4 L4 16', c: 'wood' },
  ],
  // 궁: 나무 활, 시위, 메긴 화살(쇠 촉).
  bow: [
    { d: 'M6 3 C15 6 15 14 6 17', c: 'wood' },
    { d: 'M6 3 L6 17', c: 'ink', w: 1 },
    { d: 'M3 10 L16 10', c: 'ink', w: 1.3 },
    { d: 'M13.5 7.8 L16.5 10 L13.5 12.2 Z', c: 'steel', fill: true, w: 0.8 },
  ],
  // 기병: 말 머리(왼쪽을 본다) — 귀, 이마에서 주둥이, 목.
  horse: [
    {
      d: 'M15.5 17 L8 17 C8.3 14.6 9.4 13 11 12 L7.2 12.9 L4.4 10.8 L8.3 5.8 L8.6 2.8 L11.4 5 C15.2 6 16.6 10.5 15.5 17 Z',
      c: 'hide',
      fill: true,
      w: 1,
    },
  ],
  // 민병: 쇠스랑(쇠 세 갈래에 나무 자루) — 농기구를 들고 나온 사람들.
  levy: [
    { d: 'M10 17 L10 8.5', c: 'wood' },
    { d: 'M6 3.5 L6 7 Q6 8.5 10 8.5 Q14 8.5 14 7 L14 3.5', c: 'steel' },
    { d: 'M10 3.5 L10 8.5', c: 'steel' },
  ],
};

/** 그 가지의 그림이 있는가(모르는 가지를 넘겨도 터지지 않게 — 저장에서 온 값일 수 있다). */
export function hasBranchIcon(branch: string): branch is Branch {
  return Object.hasOwn(SHAPES, branch);
}

/**
 * 가지의 기호 그림(`px` × `px`). 꾸밈이라 화면 읽기에는 감춘다(`aria-hidden`) — 읽어 주는 말은 쓰는 쪽이 붙인다.
 * 색은 획마다 토큰(`--bi-*`)에서 온다. 토큰이 없는 자리에서는 둘레의 글자색으로 그린다(`currentColor`).
 */
export function branchIcon(branch: Branch, px = 16): SVGSVGElement {
  const svg = document.createElementNS(SVG, 'svg');
  svg.setAttribute('viewBox', '0 0 20 20');
  svg.setAttribute('width', String(px));
  svg.setAttribute('height', String(px));
  svg.setAttribute('class', `branch-icon ${branch}`);
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  for (const s of SHAPES[branch]) {
    const path = document.createElementNS(SVG, 'path');
    path.setAttribute('d', s.d);
    path.setAttribute('stroke-width', String(s.w ?? 1.8));
    // 색은 스타일로 준다 — 속성으로는 `var()`가 듣지 않는다. 토큰이 없으면 글자색이다.
    const color = `var(--bi-${s.c}, currentColor)`;
    path.style.stroke = color;
    if (s.fill) path.style.fill = color;
    svg.append(path);
  }
  return svg;
}

/** 시험·검산용: 가지마다 획이 쓰는 색 이름(획의 순서대로). */
export function branchIconTones(branch: Branch): readonly string[] {
  return SHAPES[branch].map((s) => s.c);
}
